// src/components/lists/BeingsList/beingsListStyles.ts
// BeingsList-specific styled components + shared base re-exports

import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Client } from '../../../services/api';
import { ExtendedPet } from './beingsListTypes';

// Import from base and re-export with aliases
import {
  slideInFromRight,
  ListContainer,
  ListRow,
  RowClickableArea,
  ItemNameSection,
  ItemIcon,
  ItemWarningIcon,
  StillOwnedIcon,
  ItemName,
  SkeletonItemName,
  ActionButtons,
  DetailsSection,
  DetailSection,
  DetailItem,
  CopyButton,
} from '../base/ListStyles';

// Export with original names
export {
  slideInFromRight,
  ListContainer,
  ListRow,
  RowClickableArea,
  ItemNameSection,
  ItemIcon,
  ItemWarningIcon,
  StillOwnedIcon,
  ItemName,
  SkeletonItemName,
  ActionButtons,
  CopyButton,
};

// Export with backward-compatible aliases for old component names
export const BeingRow = ListRow;
export const BeingNameSection = ItemNameSection;
export const BeingIcon = ItemIcon;
export const BeingName = ItemName;
export const SkeletonBeingName = SkeletonItemName;

// Export as ContactDetailsSection for backward compatibility
export const ContactDetailsSection = DetailsSection;
export const ContactSection = DetailSection;
export const ContactItem = DetailItem;

// BeingsList-specific styled components

// Styled component for pet count display
export const PetCountDisplay = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen?: boolean }>(({ theme, isOpen }) => ({
  fontSize: '2.25rem',
  fontWeight: 400,
  fontStyle: 'italic',
  color: isOpen
    ? theme.palette.mode === 'dark'
      ? theme.palette.primary.main
      : theme.palette.primary.contrastText
    : theme.palette.mode === 'dark'
      ? theme.palette.grey[100]
      : theme.palette.grey[800],
  marginLeft: '1rem',
  opacity: isOpen ? 1 : 1,
  transition: 'opacity 0.3s ease',
  whiteSpace: 'nowrap',
}));

// RESERVATIONS - MOBILE FIRST
export const ReservationsSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen?: boolean }>(({ isOpen }) => ({
  position: 'relative',
  paddingTop: '0',
  paddingBottom: '0',
  maxHeight: isOpen ? 'auto' : '0',
  overflow: 'hidden',
  transition: 'max-height 0.3s ease-out, padding 0.3s ease-out',
  marginTop: isOpen ? '2.8rem' : '0',
  display: 'block',
  '@media (min-width: 600px)': {
    display: 'block',
    marginTop: isOpen ? '2.9rem' : '0',
  },
}));

export const ReservationsList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

// RESERVATIONS ITEM ROW
export const ReservationItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '0.3rem 0.1rem',
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.primary.dark
      : theme.palette.primary.contrastText,
  opacity: 0.9,
  '& p': {
    opacity: 1,
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.primary.dark
        : theme.palette.primary.contrastText,
  },
  '&:hover': {
    cursor: 'pointer',
    backgroundColor: theme.palette.primary.main,
  },
  '&:first-of-type': {
    borderTop: `1px solid ${
      theme.palette.mode === 'dark'
        ? theme.palette.primary.dark
        : theme.palette.primary.contrastText
    }`,
  },
  '&:last-of-type': {
    borderBottom: `1px solid ${
      theme.palette.mode === 'dark'
        ? theme.palette.primary.dark
        : theme.palette.primary.contrastText
    }`,
  },
  '&:nth-of-type(odd)': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(28, 46, 74, 0.2)'
        : theme.palette.grey[700],
    '& p': {
      color:
        theme.palette.mode === 'dark'
          ? theme.palette.primary.dark
          : theme.palette.grey[100],
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      '& p': { color: theme.palette.primary.contrastText },
    },
  },
  '&:nth-of-type(even)': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(28, 46, 74, 0.1)'
        : theme.palette.grey[600],
    '& p': {
      color:
        theme.palette.mode === 'dark'
          ? theme.palette.primary.dark
          : theme.palette.grey[100],
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      '& p': { color: theme.palette.primary.contrastText },
    },
  },
  '@media (min-width: 600px)': { padding: '0.3rem 4rem' },
}));

export const ReservationDate = styled(Typography)(({ theme }) => ({
  fontSize: '1.6rem',
  fontWeight: 500,
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.primary.dark
      : theme.palette.primary.contrastText,
  marginLeft: '8px',
  '@media (min-width: 600px)': { fontSize: '1.8rem' },
}));

export const ReservationColumns = styled('div')({
  display: 'flex',
});

export const InlineFlexColumn = styled('div')({
  width: '12rem',
  padding: 0,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  '&:first-of-type': {
    textAlign: 'right',
    width: '10.5rem',
  },
});

// Clients/pets list (for ClientPetsList)
export const ClientsPetsDetailsSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen?: boolean }>(({ isOpen }) => ({
  position: 'relative',
  paddingLeft: '1.25rem',
  paddingRight: '1.25rem',
  paddingTop: isOpen ? '1rem' : '0',
  paddingBottom: isOpen ? '1.25rem' : '0',
  maxHeight: isOpen ? '200px' : '0',
  overflow: 'hidden',
  transition: 'max-height 0.3s ease-out, padding 0.3s ease-out',
}));

export const ClientsPetsSection = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  gap: '32px',
  alignItems: 'center',
  flexWrap: 'wrap',
  width: '100%',
  marginTop: '6rem',
});

export const ClientsPetsItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.primary.dark
      : theme.palette.primary.contrastText,
  padding: '8px 0',
  '& .MuiTypography-root': {
    fontSize: '2.25rem',
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.primary.dark
        : theme.palette.primary.contrastText,
  },
  '& .MuiSvgIcon-root': {
    fontSize: '2.25rem',
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.primary.main
        : theme.palette.primary.contrastText,
  },
}));

// Helper function to check if being data is loaded
export const isBeingDataLoaded = (
  item: Client | ExtendedPet,
  type: 'clients' | 'pets'
): boolean => {
  if (type === 'clients') {
    const client = item as Client;
    return !!(client.lastName && client.firstName);
  } else {
    const pet = item as ExtendedPet;
    return !!pet.name;
  }
};
